/**
 * UpcomingPayments Component
 *
 * Displays a list of upcoming payments with due dates and amounts
 * from the getMyDashboard.upcomingPayments data.
 *
 * Requirements: 7.1, 7.2 - Upcoming payments widget on dashboard
 */

import { Card, CardContent, CardHeader, CardTitle } from '@bloomcode/ui';
import { CreditCard, Calendar, AlertCircle } from '@bloomcode/icons';

export interface UpcomingPaymentItem {
  id: string;
  amount: number | string;
  dueDate: string;
  description: string;
  status: string;
}

interface UpcomingPaymentsProps {
  payments: UpcomingPaymentItem[];
  isLoading?: boolean;
}

function UpcomingPaymentsSkeleton() {
  return (
    <Card>
      <CardHeader>
        <div className="h-5 w-40 animate-pulse rounded bg-muted" />
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-2">
                <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                <div className="h-3 w-24 animate-pulse rounded bg-muted" />
              </div>
              <div className="h-5 w-16 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function formatCurrency(amount: number | string): string {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(numAmount);
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getDaysUntilDue(dateString: string): number {
  const dueDate = new Date(dateString);
  const now = new Date();
  const diffMs = dueDate.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function getDueLabel(dateString: string): { text: string; urgent: boolean } {
  const days = getDaysUntilDue(dateString);
  if (days < 0) return { text: 'Overdue', urgent: true };
  if (days === 0) return { text: 'Due today', urgent: true };
  if (days === 1) return { text: 'Due tomorrow', urgent: true };
  if (days <= 7) return { text: `Due in ${days} days`, urgent: true };
  return { text: formatDate(dateString), urgent: false };
}

interface PaymentRowProps {
  payment: UpcomingPaymentItem;
}

function PaymentRow({ payment }: PaymentRowProps) {
  const dueInfo = getDueLabel(payment.dueDate);

  return (
    <div className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-muted/50">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CreditCard className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{payment.description}</p>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span className={dueInfo.urgent ? 'text-destructive font-medium' : ''}>
              {dueInfo.text}
            </span>
          </div>
        </div>
      </div>
      <span className="text-sm font-semibold whitespace-nowrap">
        {formatCurrency(payment.amount)}
      </span>
    </div>
  );
}

export function UpcomingPayments({ payments, isLoading }: UpcomingPaymentsProps) {
  if (isLoading) {
    return <UpcomingPaymentsSkeleton />;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-lg">Upcoming Payments</CardTitle>
        {payments.length > 0 && (
          <span className="text-xs text-muted-foreground">{payments.length} pending</span>
        )}
      </CardHeader>
      <CardContent>
        {payments.length === 0 ? (
          <div className="flex flex-col items-center py-6 text-center text-muted-foreground">
            <AlertCircle className="mb-2 h-8 w-8 opacity-50" />
            <p className="text-sm">No upcoming payments</p>
          </div>
        ) : (
          <div className="space-y-2">
            {payments.map((payment) => (
              <PaymentRow key={payment.id} payment={payment} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default UpcomingPayments;
